import { BlocksByJobResponse } from "protos/portal/spatial";
import { getOrPut } from "common/utils/maps";
import { isNil } from "../../utils/identity";
import {
  JigsawBlocksByJobResponse,
  JigsawBlocksCrop,
  JigsawBlocksResponse,
  JigsawBlocksWeed,
} from "protos/portal/jigsaw";
import { portalApi } from "portal/state/portalApi";
import { RobotBlockHistory } from "portal/views/robots/useRobotHistory";
import { useEffect, useRef } from "react";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import { useLazyPopups } from "portal/utils/hooks/useApiPopups";
import { useMemoAsync } from "portal/utils/hooks/useMemoAsync";
import { useStableArray } from "portal/utils/hooks/useStable";
import { useTranslation } from "react-i18next";

type SpatialEntry = BlocksByJobResponse["blocks"][string];
type JigsawEntry = JigsawBlocksByJobResponse["blocks"][string];

/**
 * The per-jobId block map, discriminated by metric mode. Exposed so consumers
 * (e.g. the GeoJSON export) can attribute each block to its job without a
 * second fetch. Mirrors the two shapes the block endpoints return.
 */
export type BlocksByJobIdMap =
  | { type: "old"; map: Record<string, SpatialEntry | undefined> }
  | { type: "jigsaw"; map: Record<string, JigsawEntry | undefined> };

interface HeatmapBlocks {
  blocks: RobotBlockHistory | undefined;
  blockLabels: string[];
  blocksByJobId: BlocksByJobIdMap | undefined;
  isLoading: boolean;
}

/**
 * Batch fetches block data and then constructs data needed for the map
 * [a flat array of the blocks and labels to go with them]
 *
 * Blocks are cached per-jobId for the lifetime of the hook so that toggling
 * a job off (or back on) doesn't trigger a network round-trip — only ids
 * not yet in the cache are fetched. The cache is reset when `useJigsaw`
 * flips since the two endpoints return different block shapes. In-progress
 * jobs would not see new blocks until the page reloads.
 */
export const useHeatmapBlocks = (
  selectedJobIds: string[],
  jobNames?: Map<string, string>
): HeatmapBlocks => {
  const { i18n } = useTranslation();
  const useJigsaw = useJigsawMetrics();

  const [batchGetJobSpatialBlocks] = useLazyPopups(
    portalApi.endpoints.batchGetJobSpatialBlocks.useLazyQuery(),
    { errorVariant: "warning" }
  );
  const [batchGetJobJigsawBlocks] = useLazyPopups(
    portalApi.endpoints.batchGetJobJigsawBlocks.useLazyQuery(),
    { errorVariant: "warning" }
  );

  const stableJobIds = useStableArray(selectedJobIds);

  // Values may be undefined when the server returned no entry for a job id —
  // we still record the lookup so we don't re-fetch it.
  const spatialCacheRef = useRef<Map<string, SpatialEntry | undefined>>(
    new Map()
  );
  const jigsawCacheRef = useRef<Map<string, JigsawEntry | undefined>>(
    new Map()
  );
  useEffect(() => {
    spatialCacheRef.current.clear();
    jigsawCacheRef.current.clear();
  }, [useJigsaw]);

  const [fetchResult, { isLoading }] = useMemoAsync<{
    history: RobotBlockHistory | undefined;
    labels: string[];
    blocksByJobId: BlocksByJobIdMap | undefined;
  }>(
    async () => {
      if (stableJobIds.length === 0) {
        return { history: undefined, labels: [], blocksByJobId: undefined };
      }

      const cacheKey = (id: string): string => `${i18n.language}-${id}`;

      if (useJigsaw) {
        const cache = jigsawCacheRef.current;
        const missing = stableJobIds.filter((id) => !cache.has(cacheKey(id)));
        if (missing.length > 0) {
          const { data } = await batchGetJobJigsawBlocks(
            {
              jobIds: missing,
              includeHardwareState: true,
              language: i18n.language,
            },
            true
          );
          if (!data) {
            return { history: undefined, labels: [], blocksByJobId: undefined };
          }
          for (const id of missing) {
            cache.set(cacheKey(id), data.blocks[id]);
          }
        }
        const blocksByJobId: Record<string, JigsawEntry | undefined> = {};
        for (const id of stableJobIds) {
          blocksByJobId[id] = cache.get(cacheKey(id));
        }
        const { allBlocks, labels } = flattenBlocksWithJobLabels(
          stableJobIds,
          blocksByJobId,
          jobNames
        );
        const { crops, weeds } = flattenCropsAndWeeds(
          stableJobIds,
          blocksByJobId
        );
        return {
          history: {
            type: "jigsaw" as const,
            jigsawBlocks: allBlocks,
            crops,
            weeds,
          },
          labels,
          blocksByJobId: { type: "jigsaw" as const, map: blocksByJobId },
        };
      }

      const cache = spatialCacheRef.current;
      const missing = stableJobIds.filter((id) => !cache.has(cacheKey(id)));
      if (missing.length > 0) {
        const { data } = await batchGetJobSpatialBlocks(
          { jobIds: missing, language: i18n.language },
          true
        );
        if (!data) {
          return { history: undefined, labels: [], blocksByJobId: undefined };
        }
        for (const id of missing) {
          cache.set(cacheKey(id), data.blocks[id]);
        }
      }
      const blocksByJobId: Record<string, SpatialEntry | undefined> = {};
      for (const id of stableJobIds) {
        blocksByJobId[id] = cache.get(cacheKey(id));
      }
      const { allBlocks, labels } = flattenBlocksWithJobLabels(
        stableJobIds,
        blocksByJobId,
        jobNames
      );
      return {
        history: { type: "old" as const, blocks: allBlocks },
        labels,
        blocksByJobId: { type: "old" as const, map: blocksByJobId },
      };
    },
    [
      stableJobIds,
      useJigsaw,
      batchGetJobSpatialBlocks,
      batchGetJobJigsawBlocks,
      jobNames,
      i18n.language,
    ],
    { history: undefined, labels: [], blocksByJobId: undefined }
  );

  return {
    blocks: fetchResult.history,
    blockLabels: fetchResult.labels,
    blocksByJobId: fetchResult.blocksByJobId,
    isLoading,
  };
};

const flattenBlocksWithJobLabels = <TBlock>(
  stableJobIds: string[],
  blocksByJobId: Record<string, { blocks?: readonly TBlock[] } | undefined>,
  jobNames?: Map<string, string>
): { allBlocks: TBlock[]; labels: string[] } => {
  const labels: string[] = [];
  const allBlocks: TBlock[] = [];
  for (const jobId of stableJobIds) {
    const name = jobNames?.get(jobId) ?? jobId;
    const entry = blocksByJobId[jobId];
    if (!entry?.blocks?.length) {
      continue;
    }
    for (const block of entry.blocks) {
      allBlocks.push(block);
      labels.push(name);
    }
  }
  return { allBlocks, labels };
};

/**
 * @returns the list of all crops and all weeds in all the blocks responses.
 * Identical crops and weeds have additional stars merged. The final results
 * are sorted by counts in descending order.
 *
 * NOTE: Ideally this would happen in the backend, but we don't actually
 * query for all jobs at a single time, so we have to do it here
 **/
const flattenCropsAndWeeds = (
  stableJobIds: string[],
  blocksByJobId: Record<string, JigsawBlocksResponse | undefined>
): {
  crops: JigsawBlocksCrop[];
  weeds: JigsawBlocksWeed[];
} => {
  const crops = new Map<string, JigsawBlocksCrop>();
  const weeds = new Map<string, JigsawBlocksWeed>();
  for (const jobId of stableJobIds) {
    const entry = blocksByJobId[jobId];
    if (isNil(entry)) {
      continue;
    }
    entry.crops.forEach((crop) => {
      const entry = getOrPut(crops, crop.id, () =>
        JigsawBlocksCrop.fromPartial({
          id: crop.id,
          localizedName: crop.localizedName,
        })
      );
      entry.found += crop.found;
      entry.hasThinning ||= crop.hasThinning;
    });
    entry.weeds.forEach((weed) => {
      const entry = getOrPut(weeds, weed.id, () =>
        JigsawBlocksWeed.fromPartial({
          id: weed.id,
          localizedName: weed.localizedName,
        })
      );
      entry.fractionOfTotal += weed.fractionOfTotal;
    });
  }
  return {
    crops: [...crops.values()].sort((a, b) => b.found - a.found),
    weeds: [...weeds.values()].sort(
      (a, b) => b.fractionOfTotal - a.fractionOfTotal
    ),
  };
};
